'use client';

import { useAuthContext } from '@/core/providers/AuthProvider';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Shield } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/i18n';

/**
 * Persistent top banner displayed when a super admin is impersonating
 * a chain owner or hotel manager. Provides a single-click return to admin.
 */
export function ImpersonationBanner() {
  const { user, isImpersonating, returnToAdmin } = useAuthContext();
  const router = useRouter();
  const { t } = useTranslation();

  if (!isImpersonating || !user) return null;

  const handleReturn = async () => {
    const restored = await returnToAdmin();
    router.push(restored ? '/admin' : '/auth/login');
  };

  return (
    <div
      id="platform-impersonation-banner"
      className={cn(
        'w-full h-10 flex items-center justify-center gap-3',
        'bg-hms-amber/15 border-b border-hms-amber/30',
        'text-hms-amber text-xs font-medium',
        'px-4'
      )}
    >
      <Shield className="w-3.5 h-3.5" />
      <span>
        {t('impersonation.viewingAs')} <strong>{user.lastName}</strong> &mdash; {user.role.replace(/_/g, ' ')}
      </span>
      <span className="text-hms-amber/50 mx-1">|</span>
      <span className="text-hms-textDim">{t('impersonation.noAuditLogs')}</span>
      <button
        id="platform-impersonation-return"
        type="button"
        onClick={handleReturn}
        className={cn(
          'ml-3 flex items-center gap-1.5 px-3 py-1 rounded-lg',
          'bg-hms-amber/20 border border-hms-amber/30',
          'text-hms-amber text-xs font-semibold',
          'hover:bg-hms-amber/30 transition-colors duration-150'
        )}
      >
        <ArrowLeft className="w-3 h-3" />
        {t('impersonation.returnToAdmin')}
      </button>
    </div>
  );
}
